import React, { memo, useMemo, useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker, Polyline } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['geometry'];

// Simplified map options for faster rendering
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: { position: 3 },
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'cooperative',
  // Disable expensive features
  tilt: 0,
  rotateControl: false,
  scaleControl: false
};

// Loading placeholder - much lighter than a spinner
const MapPlaceholder = ({ height = '100%' }) => (
  <div 
    style={{ height, width: '100%' }} 
    className="bg-gray-100 flex items-center justify-center"
  >
    <div className="text-gray-400 text-sm">Loading map...</div>
  </div>
);

/**
 * LazyGoogleMap - A lightweight, optimized Google Maps component for modals/popups
 * Features:
 * - Lazy loading to avoid blocking initial render
 * - Simplified rendering for better performance
 * - Shared API loader across all instances
 */
const LazyGoogleMap = ({
  center = { lat: 29.7604, lng: -95.3698 },
  zoom = 14,
  style = { height: '100%', width: '100%' },
  polygon = null,
  polygonOptions = {},
  additionalPolygons = [],
  markers = [],
  polylines = [],
  houseLocations = [],
  rectangles = [],
  delayLoad = 100 // Delay before loading to let modal animation finish
}) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  
  // Delay loading to prevent blocking UI
  useEffect(() => {
    const timer = setTimeout(() => setShouldLoad(true), delayLoad);
    return () => clearTimeout(timer);
  }, [delayLoad]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Convert polygon from [[lat, lng], ...] to [{ lat, lng }, ...]
  const polygonPath = useMemo(() => {
    if (!polygon || polygon.length < 3) return null;
    return polygon.map(p => ({ lat: p[0], lng: p[1] }));
  }, [polygon]);

  const defaultPolygonOptions = useMemo(() => ({
    fillColor: '#8b5cf6',
    fillOpacity: 0.2,
    strokeColor: '#8b5cf6',
    strokeWeight: 2,
    clickable: false,
    ...polygonOptions
  }), [polygonOptions]);

  // Canvas overlay for house dots (performance)
  useEffect(() => {
    if (!mapRef.current || !houseLocations.length || !window.google) return;
    const map = mapRef.current;

    class DotsOverlay extends window.google.maps.OverlayView {
      constructor() { super(); this.canvas = document.createElement('canvas'); this.canvas.style.position = 'absolute'; this.canvas.style.pointerEvents = 'none'; this.canvas.style.zIndex = '999'; }
      onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.canvas); }
      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const bounds = this.getMap().getBounds();
        if (!bounds) return;
        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
        const width = Math.abs(ne.x - sw.x);
        const height = Math.abs(ne.y - sw.y);
        this.canvas.width = width; this.canvas.height = height;
        this.canvas.style.left = `${sw.x}px`; this.canvas.style.top = `${ne.y}px`;
        this.canvas.style.width = `${width}px`; this.canvas.style.height = `${height}px`;
        const ctx = this.canvas.getContext('2d');
        ctx.clearRect(0, 0, width, height);
        for (const house of houseLocations) {
          if (!house.lat || !house.lon) continue;
          const latLng = new window.google.maps.LatLng(house.lat, house.lon);
          if (!bounds.contains(latLng)) continue;
          const pixel = projection.fromLatLngToDivPixel(latLng);
          ctx.beginPath(); ctx.arc(pixel.x - sw.x, pixel.y - ne.y, 5, 0, 2 * Math.PI);
          ctx.fillStyle = '#22c55e'; ctx.fill(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
        }
      }
      onRemove() { if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas); }
    }

    const overlay = new DotsOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;
    const listeners = [
      map.addListener('zoom_changed', () => overlay.draw()),
      map.addListener('bounds_changed', () => overlay.draw()),
    ];
    return () => { overlay.setMap(null); listeners.forEach(l => window.google.maps.event.removeListener(l)); overlayRef.current = null; };
  }, [mapRef.current, houseLocations]);

  // Canvas overlay for address markers when count is large (>100)
  const useCanvasMarkers = markers.length > 100;
  useEffect(() => {
    if (!mapRef.current || !useCanvasMarkers || !window.google) return;
    const map = mapRef.current;

    class MarkersOverlay extends window.google.maps.OverlayView {
      constructor() { super(); this.canvas = document.createElement('canvas'); this.canvas.style.position = 'absolute'; this.canvas.style.pointerEvents = 'none'; this.canvas.style.zIndex = '1000'; }
      onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.canvas); }
      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const bounds = this.getMap().getBounds();
        if (!bounds) return;
        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
        const width = Math.abs(ne.x - sw.x);
        const height = Math.abs(ne.y - sw.y);
        this.canvas.width = width * 2; this.canvas.height = height * 2;
        this.canvas.style.left = `${sw.x}px`; this.canvas.style.top = `${ne.y}px`;
        this.canvas.style.width = `${width}px`; this.canvas.style.height = `${height}px`;
        const ctx = this.canvas.getContext('2d');
        ctx.scale(2, 2); // Retina
        ctx.clearRect(0, 0, width, height);
        const defaultColor = '#2563eb';
        const deliveredColor = '#16a34a';
        for (const m of markers) {
          if (!m.lat || !m.lng) continue;
          const latLng = new window.google.maps.LatLng(m.lat, m.lng);
          if (!bounds.contains(latLng)) continue;
          const pixel = projection.fromLatLngToDivPixel(latLng);
          const x = pixel.x - sw.x;
          const y = pixel.y - ne.y;
          const color = m.color || defaultColor;
          ctx.beginPath(); ctx.arc(x, y, 6, 0, 2 * Math.PI);
          ctx.fillStyle = color; ctx.fill();
          ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
        }
      }
      onRemove() { if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas); }
    }

    const overlay = new MarkersOverlay();
    overlay.setMap(map);
    const listeners = [
      map.addListener('zoom_changed', () => overlay.draw()),
      map.addListener('bounds_changed', () => overlay.draw()),
    ];
    return () => { overlay.setMap(null); listeners.forEach(l => window.google.maps.event.removeListener(l)); };
  }, [mapRef.current, markers, useCanvasMarkers]);

  // Auto-fit to markers bounds when there are many markers and no polygon
  useEffect(() => {
    if (!mapRef.current || !window.google || markers.length < 2 || polygonPath) return;
    const validMarkers = markers.filter(m => m.lat && m.lng);
    if (validMarkers.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    validMarkers.forEach(m => bounds.extend(new window.google.maps.LatLng(m.lat, m.lng)));
    mapRef.current.fitBounds(bounds, { top: 30, bottom: 30, left: 30, right: 30 });
  }, [mapRef.current, markers.length]);

  // Show placeholder while loading
  if (!shouldLoad || !isLoaded) {
    return <MapPlaceholder height={style.height} />;
  }

  return (
    <GoogleMap
      mapContainerStyle={style}
      center={center}
      zoom={zoom}
      options={mapOptions}
      onLoad={(map) => {
        mapRef.current = map;
        // Auto-fit to polygon bounds if polygon is provided
        if (polygonPath && polygonPath.length >= 3 && window.google) {
          const bounds = new window.google.maps.LatLngBounds();
          polygonPath.forEach(p => bounds.extend(new window.google.maps.LatLng(p.lat, p.lng)));
          
          // Ensure minimum bounds size so thin strips don't create extreme aspect ratios
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latSpan = ne.lat() - sw.lat();
          const lngSpan = ne.lng() - sw.lng();
          const minSpan = 0.005; // ~500m minimum in each direction
          
          if (latSpan < minSpan) {
            const centerLat = (ne.lat() + sw.lat()) / 2;
            bounds.extend(new window.google.maps.LatLng(centerLat + minSpan / 2, ne.lng()));
            bounds.extend(new window.google.maps.LatLng(centerLat - minSpan / 2, sw.lng()));
          }
          if (lngSpan < minSpan) {
            const centerLng = (ne.lng() + sw.lng()) / 2;
            bounds.extend(new window.google.maps.LatLng(ne.lat(), centerLng + minSpan / 2));
            bounds.extend(new window.google.maps.LatLng(sw.lat(), centerLng - minSpan / 2));
          }
          
          map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
        }
      }}
    >
      {/* Polygon overlay */}
      {polygonPath && (
        <Polygon
          paths={polygonPath}
          options={defaultPolygonOptions}
        />
      )}

      {/* Additional polygons (e.g. grid sections for parent jobs) */}
      {additionalPolygons.map((ap, idx) => {
        if (!ap.path || ap.path.length < 3) return null;
        const path = ap.path.map(p => Array.isArray(p) ? { lat: p[0], lng: p[1] } : p);
        // Calculate centroid for label
        const centroid = path.reduce((acc, p) => ({ lat: acc.lat + p.lat / path.length, lng: acc.lng + p.lng / path.length }), { lat: 0, lng: 0 });
        return (
          <React.Fragment key={`additional-polygon-${idx}`}>
            <Polygon
              paths={path}
              options={{
                fillColor: ap.fillColor || '#f97316',
                fillOpacity: ap.fillOpacity ?? 0.08,
                strokeColor: ap.strokeColor || '#f97316',
                strokeWeight: ap.strokeWeight ?? 2,
                strokeOpacity: ap.strokeOpacity ?? 0.8,
                clickable: false,
                ...ap.options
              }}
            />
            {ap.label && (
              <Marker
                position={centroid}
                icon={{
                  url: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="28" viewBox="0 0 40 28"><rect x="1" y="1" width="38" height="26" rx="6" fill="${ap.strokeColor || '#f97316'}" stroke="#fff" stroke-width="2"/><text x="20" y="19" font-family="Arial,sans-serif" font-size="13" font-weight="bold" fill="#fff" text-anchor="middle">${ap.label}</text></svg>`)}`,
                  scaledSize: { width: 40, height: 28 },
                  anchor: { x: 20, y: 14 }
                }}
                clickable={false}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Grid Section Rectangles */}
      {rectangles.map((rect, idx) => {
        if (!rect.bounds) return null;
        const colors = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', '#0891b2', '#7c2d12', '#4338ca', '#be185d', '#65a30d'];
        const color = rect.color || colors[idx % colors.length];
        const bounds = {
          north: rect.bounds[1][0],
          south: rect.bounds[0][0],
          east: rect.bounds[1][1],
          west: rect.bounds[0][1],
        };
        const center = rect.center 
          ? { lat: rect.center[0], lng: rect.center[1] }
          : { lat: (bounds.north + bounds.south) / 2, lng: (bounds.east + bounds.west) / 2 };
        return (
          <React.Fragment key={`rect-${idx}`}>
            {window.google && (
              <Polygon
                paths={[
                  { lat: bounds.south, lng: bounds.west },
                  { lat: bounds.south, lng: bounds.east },
                  { lat: bounds.north, lng: bounds.east },
                  { lat: bounds.north, lng: bounds.west },
                ]}
                options={{
                  fillColor: color,
                  fillOpacity: 0.08,
                  strokeColor: color,
                  strokeWeight: 2,
                  strokeOpacity: 0.8,
                  clickable: false,
                }}
              />
            )}
            {rect.label && (
              <Marker
                position={center}
                icon={{
                  url: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="56" height="28" viewBox="0 0 56 28"><rect x="1" y="1" width="54" height="26" rx="6" fill="${color}" stroke="#fff" stroke-width="2"/><text x="28" y="19" font-family="Arial,sans-serif" font-size="11" font-weight="bold" fill="#fff" text-anchor="middle">${rect.label}${rect.count ? ` (${rect.count})` : ''}</text></svg>`)}`,
                  scaledSize: { width: 56, height: 28 },
                  anchor: { x: 28, y: 14 }
                }}
                clickable={false}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Markers - use individual components for small sets, canvas overlay handles large sets */}
      {!useCanvasMarkers && markers.map((marker, idx) => {
        if (!marker.lat || !marker.lng) return null;
        const color = marker.color || '#8b5cf6';
        
        return (
          <Marker
            key={marker.id || idx}
            position={{ lat: marker.lat, lng: marker.lng }}
            title={marker.label || ''}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="${color}" stroke="white" stroke-width="2"/>
                  <text x="12" y="16" font-family="Arial" font-size="10" fill="white" text-anchor="middle">${marker.number || idx + 1}</text>
                </svg>
              `)}`,
              scaledSize: { width: 24, height: 24 },
              anchor: { x: 12, y: 12 }
            }}
          />
        );
      })}

      {/* Polylines - simplified */}
      {polylines.map((line, idx) => {
        if (!line.path || line.path.length < 2) return null;
        const path = line.path.map(p => 
          Array.isArray(p) ? { lat: p[0], lng: p[1] } : p
        );
        
        return (
          <Polyline
            key={idx}
            path={path}
            options={{
              strokeColor: line.color || '#f97316',
              strokeWeight: line.weight || 3,
              strokeOpacity: 0.8
            }}
          />
        );
      })}
    </GoogleMap>
  );
};

export default memo(LazyGoogleMap);
